<template>
  <div class="results-page">
    <div class="page-header">
      <h1>计算结果</h1>
      <div class="header-actions">
        <button class="btn btn-outline" @click="goBack">
          ← 返回计算器
        </button>
        <button class="btn btn-primary" @click="addToComparison">
          + 添加到对比
        </button>
      </div>
    </div>
    
    <div v-if="results" class="results-container">
      <!-- 概览卡片 -->
      <div class="overview-cards">
        <div class="overview-card original">
          <h3>原还款计划</h3>
          <div class="card-content">
            <div class="stat-item">
              <span class="label">贷款金额</span>
              <span class="value">{{ formatCurrency(results.originalPlan.loanAmount) }}</span>
            </div>
            <div class="stat-item">
              <span class="label">贷款期限</span>
              <span class="value">{{ results.originalPlan.loanTerm }}期</span>
            </div>
            <div class="stat-item">
              <span class="label">年利率</span>
              <span class="value">{{ formatPercentage(results.originalPlan.interestRate) }}%</span>
            </div>
            <div class="stat-item">
              <span class="label">还款方式</span>
              <span class="value">{{ getRepaymentMethodName(results.originalPlan.repaymentMethod) }}</span>
            </div>
            <div class="stat-item">
                <span class="label">贷款时间</span>
              <span class="value">{{ results.formData.firstPaymentYear }}年{{ results.formData.firstPaymentMonth }}月</span>
            </div>
            <div class="stat-item highlight">
              <span class="label">首月还款</span>
              <span class="value">{{ formatCurrency(results.originalPlan.monthlyPayment) }}</span>
            </div>
            <div class="stat-item highlight" v-if="results.originalPlan.monthlyDecrease">
              <span class="label">每月递减</span>
              <span class="value">{{ formatCurrency(results.originalPlan.monthlyDecrease) }}</span>
            </div>
            <div class="stat-item">
              <span class="label">利息总额</span>
              <span class="value">{{ formatCurrency(results.originalPlan.totalInterest) }}</span>
            </div>
            <div class="stat-item">
              <span class="label">本息合计</span>
              <span class="value">{{ formatCurrency(results.originalPlan.totalPayment) }}</span>
            </div>
          </div>
        </div>
        
        <div class="overview-card prepayment">
          <h3>提前还款后</h3>
          <div class="card-content">
            <div class="stat-item">
              <span class="label">剩余本金</span>
              <span class="value">{{ formatCurrency(results.prepaymentPlan.loanAmount) }}</span>
            </div>
            <div class="stat-item" v-if="results.formData.prepaymentMethod !== 'full_prepayment'">
              <span class="label">新期限</span>
              <span class="value">{{ results.prepaymentPlan.loanTerm }}期</span>
            </div>
            <div class="stat-item" v-if="results.formData.prepaymentMethod !== 'full_prepayment'">
              <span class="label">新利率</span>
              <span class="value">{{ formatPercentage(results.prepaymentPlan.interestRate) }}%</span>
            </div>
            <div class="stat-item" v-if="results.formData.prepaymentMethod !== 'full_prepayment'">
              <span class="label">新方式</span>
              <span class="value">{{ getRepaymentMethodName(results.prepaymentPlan.repaymentMethod) }}</span>
            </div>
            <div class="stat-item">
              <span class="label">提前还款日期</span>
              <span class="value">{{ results.formData.prepaymentYear }}年{{ results.formData.prepaymentMonth }}月</span>
            </div>
            <div class="stat-item highlight">
              <span class="label">首月还款</span>
              <span class="value">{{ formatCurrency(results.prepaymentPlan.monthlyPayment) }}</span>
            </div>
            <div class="stat-item highlight" v-if="results.prepaymentPlan.monthlyDecrease">
              <span class="label">每月递减</span>
              <span class="value">{{ formatCurrency(results.prepaymentPlan.monthlyDecrease) }}</span>
            </div>
            <div class="stat-item">
              <span class="label">利息总额</span>
              <span class="value">{{ formatCurrency(results.prepaymentPlan.totalInterest) }}</span>
            </div>
            <div class="stat-item">
              <span class="label">本息合计</span>
              <span class="value">{{ formatCurrency(results.prepaymentPlan.totalPayment) }}</span>
            </div>
          </div>
        </div>
        
        <div class="overview-card savings">
          <h3>节省情况</h3>
          <div class="card-content">
            <div class="stat-item highlight save">
              <span class="label">节省利息</span>
              <span class="value">{{ formatCurrency(results.comparison.interestSaved) }}</span>
            </div>
            <div class="stat-item highlight save">
              <span class="label">节省总额</span>
              <span class="value">{{ formatCurrency(results.comparison.totalSaved) }}</span>
            </div>
            <div class="stat-item highlight save">
              <span class="label">节省比例</span>
              <span class="value">{{ results.comparison.savingPercentage }}%</span>
            </div>
            <div class="savings-chart">
              <div class="chart-bar">
                <div class="bar-label">原计划</div>
                <div class="bar-container">
                  <div class="bar original-bar" :style="{ width: '100%' }"></div>
                </div>
                <div class="bar-value">{{ formatCurrency(results.originalPlan.totalPayment) }}</div>
              </div>
              <div class="chart-bar">
                <div class="bar-label">新计划</div>
                <div class="bar-container">
                  <div class="bar new-bar" :style="{ width: getBarWidth() + '%' }"></div>
                </div>
                <div class="bar-value">{{ formatCurrency(results.prepaymentPlan.totalPayment) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 详细还款计划表 -->
      <div class="schedule-section">
        <h2>详细还款计划表</h2>
        <div class="schedule-controls">
          <div class="control-group">
            <label>显示期数：</label>
            <select v-model="displayPeriods" @change="updateDisplayedSchedule">
              <option value="all">全部期数</option>
              <option value="6">前6期</option>
              <option value="12">前12期</option>
              <option value="24">前24期</option>
              <option value="60">前60期</option>
            </select>
          </div>
          <div class="control-group">
            <label>计划类型：</label>
            <select v-model="scheduleType" @change="updateDisplayedSchedule">
              <option value="original">原还款计划</option>
              <option value="prepayment">提前还款后计划</option>
            </select>
          </div>
        </div>
        
        <div class="schedule-table-container">
          <table class="schedule-table">
            <thead>
              <tr>
                <th>期数</th>
                <th>还款日期</th>
                <th>月供</th>
                <th>本金</th>
                <th>利息</th>
                <th>剩余本金</th>
                <th>累计利息</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(payment, index) in displayedSchedule" :key="index" 
                  :class="{ 'prepayment-row': payment.isPrepayment && payment.prepaymentType === 'prepayment' }">
                <td class="period">
                  <span v-if="!(payment.isPrepayment && payment.prepaymentType === 'prepayment')">{{ payment.period }}</span>
                </td>
                <td class="date">
                  <span v-if="!(payment.isPrepayment && payment.prepaymentType === 'prepayment')">{{ payment.date }}</span>
                </td>
                <td class="amount" :class="{ 'prepayment-amount': payment.isPrepayment && payment.prepaymentType === 'prepayment' }">
                  <span v-if="payment.isPrepayment && payment.prepaymentType === 'prepayment'" class="prepayment-indicator">提前还款</span>
                  <span v-else>{{ formatCurrency(payment.monthlyPayment) }}</span>
                </td>
                <td class="amount" :class="{ 'prepayment-amount': payment.isPrepayment && payment.prepaymentType === 'prepayment' }">
                  <span v-if="payment.isPrepayment && payment.prepaymentType === 'prepayment'">{{ formatCurrency(payment.prepaymentAmount) }}</span>
                  <span v-else>{{ formatCurrency(payment.principal) }}</span>
                </td>
                <td class="amount" :class="{ 'prepayment-amount': payment.isPrepayment && payment.prepaymentType === 'prepayment' }">
                  <span v-if="payment.isPrepayment && payment.prepaymentType === 'prepayment'">-</span>
                  <span v-else>{{ formatCurrency(payment.interest) }}</span>
                </td>
                <td class="amount" :class="{ 'prepayment-amount': payment.isPrepayment && payment.prepaymentType === 'prepayment' }">
                  {{ formatCurrency(payment.remainingPrincipal) }}
                </td>
                <td class="amount" :class="{ 'prepayment-amount': payment.isPrepayment && payment.prepaymentType === 'prepayment' }">
                  <span v-if="payment.isPrepayment && payment.prepaymentType === 'prepayment'">-</span>
                  <span v-else>{{ formatCurrency(getCumulativeInterest(index)) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="schedule-summary">
          <div class="summary-item">
            <span class="label">{{ displayPeriods === 'all' ? '总期数' : '显示还款期数' }}：</span>
            <span class="value">{{ getDisplayedPeriods() }}期</span>
          </div>
          <div class="summary-item">
            <span class="label">{{ displayPeriods === 'all' ? '总还款' : '显示期数还款' }}：</span>
            <span class="value">{{ formatCurrency(getDisplayedPayment()) }}</span>
          </div>
          <div class="summary-item">
            <span class="label">{{ displayPeriods === 'all' ? '总利息' : '显示期数利息' }}：</span>
            <span class="value">{{ formatCurrency(getDisplayedInterest()) }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div v-else class="no-results">
      <p>没有计算结果，请返回计算器进行计算</p>
      <button class="btn btn-primary" @click="goBack">返回计算器</button>
    </div>
  </div>
</template>

<script>
import { mortgageCalculator } from '../utils/calculator'

export default {
  name: 'Results',
  data() {
    return {
      displayPeriods: 'all',
      scheduleType: 'prepayment',
      displayedSchedule: []
    }
  },
  computed: {
    results() {
      return this.$route.params.results || null
    },
    currentSchedule() {
      if (!this.results) return []
      return this.scheduleType === 'original' 
        ? this.results.originalPlan.paymentSchedule 
        : this.results.prepaymentPlan.paymentSchedule
    }
  },
  mounted() {
    this.updateDisplayedSchedule()
  },
  methods: {
    updateDisplayedSchedule() {
      if (!this.currentSchedule) return
      
      if (this.displayPeriods === 'all') {
        this.displayedSchedule = this.currentSchedule
      } else {
        const periods = parseInt(this.displayPeriods)
        // 需要考虑提前还款行，确保显示正确的期数
        const filteredSchedule = this.getFilteredSchedule()
        const targetPeriods = Math.min(periods, filteredSchedule.length)
        
        // 找到第targetPeriods期在原始schedule中的位置
        let count = 0
        let endIndex = 0
        for (let i = 0; i < this.currentSchedule.length; i++) {
          const payment = this.currentSchedule[i]
          if (!(payment.isPrepayment && payment.prepaymentType === 'prepayment')) {
            count++
            if (count === targetPeriods) {
              endIndex = i + 1
              break
            }
          }
        }
        
        this.displayedSchedule = this.currentSchedule.slice(0, endIndex)
      }
    },
    
    getCumulativeInterest(index) {
      let cumulative = 0
      for (let i = 0; i <= index; i++) {
        const payment = this.displayedSchedule[i]
        // 排除提前还款行的利息计算
        if (!(payment.isPrepayment && payment.prepaymentType === 'prepayment')) {
          cumulative += payment.interest || 0
        }
      }
      return cumulative
    },
    
    // 过滤掉“提前还款”行
    getFilteredSchedule() {
      if (!this.currentSchedule) return []
      return this.currentSchedule.filter(p => !(p.isPrepayment && p.prepaymentType === 'prepayment'))
    },
    
    getTotalPeriods() {
      return this.getFilteredSchedule().length
    },
    
    getTotalInterest() {
      return this.getFilteredSchedule().reduce((sum, payment) => sum + (payment.interest || 0), 0)
    },
    
    getTotalPayment() {
      return this.getFilteredSchedule().reduce((sum, payment) => sum + (payment.monthlyPayment || 0), 0)
    },
    
    // 获取当前显示的期数（根据选择的显示期数）
    getDisplayedPeriods() {
      if (this.displayPeriods === 'all') {
        return this.getFilteredSchedule().length
      } else {
        const periods = parseInt(this.displayPeriods)
        const filteredSchedule = this.getFilteredSchedule()
        return Math.min(periods, filteredSchedule.length)
      }
    },
    
    // 获取当前显示期数的利息总额
    getDisplayedInterest() {
      let totalInterest = 0
      let normalPeriodCount = 0
      const targetPeriods = this.displayPeriods === 'all' ? Infinity : parseInt(this.displayPeriods)
      
      for (const payment of this.displayedSchedule) {
        // 只计算正常还款期的利息，排除提前还款行
        if (!(payment.isPrepayment && payment.prepaymentType === 'prepayment')) {
          if (normalPeriodCount < targetPeriods) {
            totalInterest += payment.interest || 0
            normalPeriodCount++
          } else {
            break
          }
        }
      }
      
      return totalInterest
    },
    
    // 获取当前显示期数的还款总额
    getDisplayedPayment() {
      let totalPayment = 0
      let normalPeriodCount = 0
      const targetPeriods = this.displayPeriods === 'all' ? Infinity : parseInt(this.displayPeriods)
      
      for (const payment of this.displayedSchedule) {
        // 只计算正常还款期的月供，排除提前还款行
        if (!(payment.isPrepayment && payment.prepaymentType === 'prepayment')) {
          if (normalPeriodCount < targetPeriods) {
            totalPayment += payment.monthlyPayment || 0
            normalPeriodCount++
          } else {
            break
          }
        }
      }
      
      return totalPayment
    },
    
    getBarWidth() {
      if (this.results.originalPlan.totalPayment === 0) return 0
      return (this.results.prepaymentPlan.totalPayment / this.results.originalPlan.totalPayment * 100).toFixed(1)
    },
    
    getRepaymentMethodName(method) {
      return method === 'equal_payment' ? '等额本息' : '等额本金'
    },
    
    formatCurrency(amount) {
      if (isNaN(amount) || amount === null || amount === undefined) {
        return '0.00'
      }
      return mortgageCalculator.formatCurrency(amount)
    },
    
    formatPercentage(rate) {
      if (isNaN(rate) || rate === null || rate === undefined) {
        return '0.00'
      }
      return parseFloat(rate).toFixed(2)
    },
    
    goBack() {
      this.$router.go(-1)
    },
    
    addToComparison() {
      if (!this.results) return
      
      const schemeName = prompt('请输入方案名称:', `方案${new Date().getTime()}`)
      if (schemeName) {
        const scheme = {
          ...this.results.formData,
          name: schemeName,
          id: Date.now(),
          results: this.results
        }
        
        // 保存到localStorage
        const savedSchemes = JSON.parse(localStorage.getItem('mortgageSchemes') || '[]')
        savedSchemes.push(scheme)
        localStorage.setItem('mortgageSchemes', JSON.stringify(savedSchemes))
        
        alert('方案已添加到对比列表')
      }
    }
  },
  watch: {
    results() {
      this.updateDisplayedSchedule()
    }
  }
}
</script>

<style scoped>
.results-page {
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.page-header h1 {
  font-size: 2.2em;
  font-weight: 300;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 15px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}

.btn-primary {
  background: rgba(255,255,255,0.2);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
}

.btn-primary:hover {
  background: rgba(255,255,255,0.3);
}

.btn-outline {
  background: transparent;
  color: white;
  border: 1px solid rgba(255,255,255,0.5);
}

.btn-outline:hover {
  background: rgba(255,255,255,0.1);
}

.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.overview-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  overflow: hidden;
}

.overview-card h3 {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  margin: 0;
  padding: 15px 20px;
  font-size: 1.2em;
  font-weight: 500;
}

.overview-card.original h3 {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.overview-card.prepayment h3 {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
}

.overview-card.savings h3 {
  background: linear-gradient(135deg, #fd7e14 0%, #e83e8c 100%);
}

.card-content {
  padding: 20px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-item.highlight {
  background: #f8f9fa;
  margin: 10px -20px;
  padding: 15px 20px;
  border-radius: 5px;
  font-weight: 600;
}

.stat-item.save .value {
  color: #28a745;
  font-size: 1.1em;
}

.label {
  color: #666;
  font-weight: 500;
}

.value {
  color: #333;
  font-weight: 600;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
}

.savings-chart {
  margin-top: 20px;
}

.chart-bar {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
}

.bar-label {
  min-width: 60px;
  font-size: 0.9em;
  color: #666;
}

.bar-container {
  flex: 1;
  height: 20px;
  background: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.bar {
  height: 100%;
  border-radius: 10px;
  transition: width 0.5s ease;
}

.original-bar {
  background: linear-gradient(90deg, #667eea, #764ba2);
}

.new-bar {
  background: linear-gradient(90deg, #28a745, #20c997);
}

.bar-value {
  min-width: 100px;
  text-align: right;
  font-size: 0.9em;
  color: #666;
}

.schedule-section {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  padding: 25px;
  margin-bottom: 30px;
}

.schedule-section h2 {
  color: #333;
  margin-bottom: 20px;
  font-size: 1.5em;
}

.schedule-controls {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.control-group label {
  font-weight: 500;
  color: #555;
}

.control-group select {
  padding: 8px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 5px;
  font-size: 14px;
}

.schedule-table-container {
  overflow-x: auto;
  margin-bottom: 20px;
  width: 100%;
}

.schedule-table {
  width: 100%;
  min-width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  table-layout: fixed;
}

.schedule-table th,
.schedule-table td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  line-height: 1.4;
  vertical-align: middle;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
}

.schedule-table th {
  background: #667eea;
  color: white;
  font-weight: 600;
  position: sticky;
  top: 0;
}

.schedule-table tr:hover {
  background: #f8f9fa;
  transform: translateX(1px);
  transition: all 0.2s ease;
}

.schedule-table tr:not(.prepayment-row):hover {
  background: #f1f5f9;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.schedule-table .period {
  text-align: center;
  font-weight: 600;
  color: #667eea;
  width: 8%;
}

.schedule-table .date {
  text-align: center;
  font-size: 13px;
  color: #666;
  width: 12%;
}

.schedule-table .amount {
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #333;
  width: 16%;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
}

/* 提前还款行样式 */
.prepayment-row {
  background: #fef2f2 !important;
  border-left: 4px solid #dc2626;
  position: relative;
}

.prepayment-row:hover {
  background: #fee2e2 !important;
  transition: background-color 0.2s ease;
}

.prepayment-indicator {
  color: #ffffff;
  font-weight: 600;
  font-size: 12px;
  background: #dc2626;
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
}

.prepayment-amount {
  color: #dc2626 !important;
  font-weight: 600 !important;
  font-size: 14px;
}

/* 提前还款行的对齐修复 */
.prepayment-row td {
  text-align: center !important;
  vertical-align: middle !important;
}

.schedule-summary {
  display: flex;
  justify-content: space-around;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  flex-wrap: wrap;
  gap: 20px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.summary-item .label {
  font-size: 0.9em;
  color: #666;
}

.summary-item .value {
  font-size: 1.1em;
  font-weight: 600;
  color: #333;
}

.no-results {
  text-align: center;
  padding: 50px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.no-results p {
  color: #666;
  margin-bottom: 20px;
  font-size: 1.1em;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .results-page {
    padding: 10px;
  }
  
  .page-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
    padding: 20px 15px;
  }
  
  .page-header h1 {
    font-size: 1.8em;
  }
  
  .header-actions {
    flex-direction: column;
    width: 100%;
    gap: 10px;
  }
  
  .btn {
    width: 100%;
    padding: 12px;
    font-size: 14px;
  }
  
  .overview-cards {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-bottom: 20px;
  }
  
  .overview-card h3 {
    font-size: 1.1em;
    padding: 12px 15px;
  }
  
  .card-content {
    padding: 15px;
  }
  
  .stat-item {
    padding: 6px 0;
  }
  
  .stat-item.highlight {
    margin: 8px -15px;
    padding: 12px 15px;
  }
  
  .stat-item .label {
    font-size: 13px;
  }
  
  .stat-item .value {
    font-size: 14px;
  }
  
  .stat-item.save .value {
    font-size: 15px;
  }
  
  .savings-chart {
    margin-top: 15px;
  }
  
  .chart-bar {
    margin-bottom: 8px;
  }
  
  .bar-label {
    min-width: 50px;
    font-size: 12px;
  }
  
  .bar-container {
    height: 15px;
  }
  
  .bar-value {
    min-width: 80px;
    font-size: 12px;
  }
  
  .schedule-section {
    padding: 15px;
    margin-bottom: 20px;
  }
  
  .schedule-section h2 {
    font-size: 1.3em;
    margin-bottom: 15px;
  }
  
  .schedule-controls {
    flex-direction: column;
    gap: 10px;
    margin-bottom: 15px;
  }
  
  .control-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .control-group label {
    font-size: 14px;
  }
  
  .control-group select {
    width: 100%;
    padding: 8px;
    font-size: 14px;
  }
  
  .schedule-table-container {
    margin-bottom: 15px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .schedule-table {
    font-size: 0.8em;
    min-width: 600px;
  }
  
  .schedule-table th,
  .schedule-table td {
    padding: 6px 4px;
    white-space: nowrap;
  }
  
  .schedule-table .amount {
    font-size: 0.9em;
    font-weight: 500;
    letter-spacing: 0.2px;
  }
  
  .schedule-table .period {
    font-size: 0.9em;
    font-weight: 600;
  }
  
  .schedule-table .date {
    font-size: 0.8em;
  }
  
  .schedule-summary {
    flex-direction: column;
    gap: 10px;
    padding: 12px;
  }
  
  .summary-item .label {
    font-size: 12px;
  }
  
  .summary-item .value {
    font-size: 14px;
  }
  
  .note {
    font-size: 12px;
    margin-top: 8px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 480px) {
  .results-page {
    padding: 5px;
  }
  
  .page-header {
    padding: 15px 10px;
  }
  
  .page-header h1 {
    font-size: 1.5em;
  }
  
  .overview-card h3 {
    font-size: 1em;
    padding: 10px 12px;
  }
  
  .card-content {
    padding: 12px;
  }
  
  .stat-item {
    padding: 4px 0;
  }
  
  .stat-item.highlight {
    margin: 6px -12px;
    padding: 10px 12px;
  }
  
  .stat-item .label {
    font-size: 12px;
  }
  
  .stat-item .value {
    font-size: 13px;
  }
  
  .schedule-section {
    padding: 12px;
  }
  
  .schedule-table {
    font-size: 0.75em;
    min-width: 500px;
  }
  
  .schedule-table th,
  .schedule-table td {
    padding: 4px 2px;
  }
  
  .schedule-summary {
    padding: 10px;
  }
  
  .summary-item .label {
    font-size: 11px;
  }
  
  .summary-item .value {
    font-size: 13px;
  }
}
</style>
